import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { AddNewProperty } from '../add-new-property/add-new-property';
import {
  OwnerPortalDashboardService,
  OwnerPortalOverviewResponse,
  OwnerPortalProfile,
  OwnerPortalUnit,
  OwnerPortalUnitI18n,
  OwnerPortalWalletResponse,
} from './services/owner-portal-dashboard.service';
import { OwnerPortalTabsService, Tab } from './services/owner-portal-tabs.service';
import { TranslatePipe } from '../../../core/pipes/translate.pipe';
import { SeasonalPricingPanel } from '../owner/Components/seasonal-pricing-panel/seasonal-pricing-panel';
import { AvailabilityComponent } from '../owner/Components/availability/availability';
import { Marketing } from '../broker/Components/broker-tabs-Components/marketing/marketing';
import { LanguageService } from '../../../core/services/language.service';

type UnitSortKey = 'title' | 'status' | 'purpose' | 'price' | 'updatedAt';

@Component({
  selector: 'app-owner-portal-dashboard',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    AddNewProperty,
    TranslatePipe,
    SeasonalPricingPanel,
    AvailabilityComponent,
    Marketing,
  ],
  templateUrl: './owner-portal-dashboard.component.html',
  styleUrls: ['./owner-portal-dashboard.component.scss'],
})
export class OwnerPortalDashboardComponent implements OnInit, OnDestroy {
  tabs: Tab[] = [];
  activeTab = 'overview';
  overview: OwnerPortalOverviewResponse | null = null;
  profile: OwnerPortalProfile | null = null;
  wallet: OwnerPortalWalletResponse | null = null;
  isLoading = false;
  errorMessage = '';
  profileForm!: FormGroup;
  payoutForm: FormGroup;
  selectedFile: File | null = null;
  profileImagePreview: string | null = null;
  saving = false;
  isEditingProfile = false;
  submitting = false;
  successMessage = '';
  payoutMessage = '';
  readonly showProfileOptions = ['public', 'registered', 'private'];
  readonly langs = ['en', 'ar'];
  readonly unitPageSizes = [8, 12, 24];

  unitSearchTerm = '';
  unitSortKey: UnitSortKey = 'updatedAt';
  unitSortDirection: 'asc' | 'desc' = 'desc';
  unitPage = 1;
  unitPageSize = 8;
  selectedUnitId: string | null = null;
  currentLanguage = 'ar';

  pricingUnitId: string | null = null;
  availabilityUnitId: string | null = null;

  private readonly subscriptions = new Subscription();

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private tabsService: OwnerPortalTabsService,
    private dashboardService: OwnerPortalDashboardService,
    private languageService: LanguageService,
  ) {
    this.payoutForm = this.fb.group({
      amount: ['', [Validators.required, Validators.min(1)]],
    });
  }

  ngOnInit(): void {
    this.tabs = this.tabsService.getTabs();
    this.activeTab = this.tabsService.getActiveTab();
    this.currentLanguage = this.languageService.getCurrentLanguage();

    this.profileForm = this.fb.group({
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      phoneNumber: ['', Validators.required],
      lang: ['en'],
      showProfile: ['public'],
      aboutMe: [''],
      location: [''],
      numberOfPropertiesOwned: ['', [Validators.pattern(/^[0-9]+$/)]],
      notificationEmail: [true],
      notificationApp: [true],
    });
    this.profileForm.disable();

    this.subscriptions.add(
      this.tabsService.activeTab$.subscribe((tabId) => {
        this.activeTab = tabId;
      }),
    );

    this.subscriptions.add(
      this.languageService.currentLanguage$.subscribe((language) => {
        this.currentLanguage = language;
      }),
    );

    this.subscriptions.add(
      this.dashboardService.overview$.subscribe((overview) => {
        this.overview = overview;
      }),
    );

    this.subscriptions.add(
      this.dashboardService.profile$.subscribe((profile) => {
        this.profile = profile;
        if (profile) {
          this.profileForm.patchValue({
            firstName: profile.firstName ?? '',
            lastName: profile.lastName ?? '',
            phoneNumber: profile.phoneNumber ?? '',
            lang: profile.lang ?? 'en',
            showProfile: profile.ShowProfile ?? 'public',
            aboutMe: profile.aboutMe ?? '',
            location: profile.location ?? '',
            numberOfPropertiesOwned: profile.numberOfPropertiesOwned ?? '',
            notificationEmail: profile.notificationSettings?.email ?? true,
            notificationApp: profile.notificationSettings?.app ?? true,
          });
        }
      }),
    );

    this.subscriptions.add(
      this.dashboardService.wallet$.subscribe((wallet) => {
        this.wallet = wallet;
      }),
    );

    this.subscriptions.add(
      this.dashboardService.isLoading$.subscribe((loading) => {
        this.isLoading = loading;
      }),
    );

    this.subscriptions.add(
      this.dashboardService.error$.subscribe((error) => {
        this.errorMessage = error;
      }),
    );

    this.dashboardService.loadOverview(true);
    this.dashboardService.loadProfile(true);
    this.dashboardService.loadWallet(true);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  setActiveTab(tabId: string): void {
    this.tabsService.setActiveTab(tabId);
  }

  refreshDashboard(): void {
    this.dashboardService.loadOverview(true);
    this.dashboardService.loadProfile(true);
    this.dashboardService.loadWallet(true);
  }

  get totalUnits(): number {
    return this.overview?.unitsCount ?? 0;
  }

  get totalBookings(): number {
    return this.overview?.bookingsCount ?? 0;
  }

  get totalRevenue(): number {
    return this.overview?.totalRevenue ?? 0;
  }

  get filteredUnits(): OwnerPortalUnit[] {
    const units = this.overview?.units ?? [];
    const term = this.unitSearchTerm.trim().toLowerCase();

    if (!term) {
      return units;
    }

    return units.filter((unit) => {
      const searchable = [
        this.getUnitTitle(unit),
        unit.unitNumber,
        unit.status,
        unit.purpose,
        this.getUnitType(unit),
        this.getI18nValue(unit.location?.country),
        this.getI18nValue(unit.location?.city),
        this.getI18nValue(unit.location?.area),
        this.getI18nValue(unit.location?.address),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return searchable.includes(term);
    });
  }

  get sortedUnits(): OwnerPortalUnit[] {
    const direction = this.unitSortDirection === 'asc' ? 1 : -1;

    return [...this.filteredUnits].sort((a, b) => {
      const first = this.getUnitSortValue(a);
      const second = this.getUnitSortValue(b);

      if (typeof first === 'number' && typeof second === 'number') {
        return (first - second) * direction;
      }

      return String(first).localeCompare(String(second)) * direction;
    });
  }

  get unitTotalPages(): number {
    return Math.max(1, Math.ceil(this.sortedUnits.length / this.unitPageSize));
  }

  get normalizedUnitPage(): number {
    return Math.min(this.unitPage, this.unitTotalPages);
  }

  get paginatedUnits(): OwnerPortalUnit[] {
    const start = (this.normalizedUnitPage - 1) * this.unitPageSize;
    return this.sortedUnits.slice(start, start + this.unitPageSize);
  }

  get unitPageStart(): number {
    if (this.sortedUnits.length === 0) return 0;
    return (this.normalizedUnitPage - 1) * this.unitPageSize + 1;
  }

  get unitPageEnd(): number {
    return Math.min(this.normalizedUnitPage * this.unitPageSize, this.sortedUnits.length);
  }

  get unitPageNumbers(): number[] {
    const total = this.unitTotalPages;
    const current = this.normalizedUnitPage;
    const start = Math.max(1, current - 2);
    const end = Math.min(total, start + 4);
    return Array.from({ length: end - start + 1 }, (_, index) => start + index);
  }

  formatCurrency(amount: number): string {
    return `EGP ${amount.toLocaleString('en-US')}`;
  }

  formatNumber(value: number): string {
    return value.toLocaleString('en-US');
  }

  setUnitSearchTerm(value: string): void {
    this.unitSearchTerm = value;
    this.unitPage = 1;
  }

  sortUnits(key: UnitSortKey): void {
    if (this.unitSortKey === key) {
      this.unitSortDirection = this.unitSortDirection === 'asc' ? 'desc' : 'asc';
      return;
    }

    this.unitSortKey = key;
    this.unitSortDirection = key === 'updatedAt' || key === 'price' ? 'desc' : 'asc';
  }

  getSortIcon(key: UnitSortKey): string {
    if (this.unitSortKey !== key) {
      return 'fa-sort';
    }

    return this.unitSortDirection === 'asc' ? 'fa-arrow-up-short-wide' : 'fa-arrow-down-wide-short';
  }

  setUnitPage(page: number): void {
    this.unitPage = Math.min(Math.max(page, 1), this.unitTotalPages);
  }

  setUnitPageSize(value: string): void {
    this.unitPageSize = Number(value) || this.unitPageSizes[0];
    this.unitPage = 1;
  }

  selectUnit(unit: OwnerPortalUnit): void {
    this.selectedUnitId = unit._id;
  }

  isUnitSelected(unit: OwnerPortalUnit): boolean {
    return this.selectedUnitId === unit._id;
  }

  trackByUnit(_: number, unit: OwnerPortalUnit): string {
    return unit._id;
  }

  /** Resolves an i18n field (object with ar/en keys or plain string) to the current language. */
  getI18nValue(field: OwnerPortalUnitI18n | string | undefined): string {
    if (!field) return '';
    if (typeof field === 'string') return field;
    return this.currentLanguage === 'ar' ? (field.ar || field.en) : (field.en || field.ar);
  }

  getUnitTitle(unit: OwnerPortalUnit): string {
    if (this.currentLanguage === 'ar') {
      return unit.title_ar || unit.title || unit.title_en || unit._id;
    }

    return unit.title_en || unit.title || unit.title_ar || unit._id;
  }

  getUnitType(unit: OwnerPortalUnit): string {
    if (this.currentLanguage === 'ar') {
      return unit.unitType_ar || unit.unitType || unit.unitType_en || '';
    }
    return unit.unitType_en || unit.unitType || unit.unitType_ar || '';
  }

  getUnitLocation(unit: OwnerPortalUnit): string {
    const city = this.getI18nValue(unit.location?.city);
    const area = this.getI18nValue(unit.location?.area);
    return [city, area].filter(Boolean).join(', ');
  }

  getUnitPrice(unit: OwnerPortalUnit): string {
    if (!unit.basePrice?.price) {
      return 'EGP 0';
    }

    return `${unit.basePrice.currency || 'EGP'} ${unit.basePrice.price.toLocaleString('en-US')}`;
  }

  getUnitSortValue(unit: OwnerPortalUnit): string | number {
    switch (this.unitSortKey) {
      case 'title':
        return this.getUnitTitle(unit).toLowerCase();
      case 'status':
        return (unit.status || 'active').toLowerCase();
      case 'purpose':
        return (unit.purpose || '').toLowerCase();
      case 'price':
        return unit.basePrice?.price ?? 0;
      case 'updatedAt':
        return unit.updatedAt ? new Date(unit.updatedAt).getTime() : 0;
      default:
        return '';
    }
  }

  getUnitStatusKey(status?: string): string {
    const normalizedStatus = (status || 'active').replace(/-/g, '_');
    const statusKeys: Record<string, string> = {
      active: 'active',
      pending: 'pending',
      inactive: 'inactive',
      draft: 'draft',
      sold: 'sold',
      reserved: 'reserved',
      rented: 'rented',
      rejected: 'rejected',
    };

    return statusKeys[normalizedStatus]
      ? `ownerPortalDashboard.overview.status.${statusKeys[normalizedStatus]}`
      : status || 'ownerPortalDashboard.overview.status.active';
  }

  getUnitPurposeKey(purpose?: string): string {
    const normalizedPurpose = (purpose || '').replace(/-/g, '_');
    const purposeKeys: Record<string, string> = {
      rent: 'rent',
      sale: 'sale',
      sale_and_rent: 'saleAndRent',
      saleAndRent: 'saleAndRent',
    };

    return purposeKeys[normalizedPurpose]
      ? `ownerPortalDashboard.overview.purpose.${purposeKeys[normalizedPurpose]}`
      : purpose || 'ownerPortalDashboard.overview.notAvailable';
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.selectedFile = input.files?.[0] ?? null;
    if (this.selectedFile) {
      const reader = new FileReader();
      reader.onload = () => (this.profileImagePreview = reader.result as string);
      reader.readAsDataURL(this.selectedFile);
    } else {
      this.profileImagePreview = null;
    }
  }

  async saveProfile(): Promise<void> {
    if (this.profileForm.invalid) {
      this.profileForm.markAllAsTouched();
      return;
    }

    this.saving = true;
    this.successMessage = '';
    this.errorMessage = '';

    try {
      const formData = new FormData();
      const value = this.profileForm.value;

      // Scalar fields
      const scalarFields = [
        'firstName',
        'lastName',
        'phoneNumber',
        'lang',
        'showProfile',
        'aboutMe',
        'location',
        'numberOfPropertiesOwned',
      ];
      scalarFields.forEach((key) => {
        const v = value[key];
        if (key === 'showProfile') return; // Backend does not allow this field in update
        if (v !== null && v !== undefined && v !== '') {
          formData.append(key, String(v));
        }
      });

      // Notification settings as nested form-data keys
      formData.append('notificationSettings[email]', String(value.notificationEmail ?? true));
      formData.append('notificationSettings[app]', String(value.notificationApp ?? true));

      if (this.selectedFile) {
        formData.append('profileImage', this.selectedFile);
      }

      await this.dashboardService.updateProfile(formData);
      this.successMessage = 'ownerPortalDashboard.profile.updateSuccess';
      this.isEditingProfile = false;
      this.profileForm.disable();
    } catch {
      this.errorMessage = 'ownerPortalDashboard.profile.updateError';
    } finally {
      this.saving = false;
    }
  }

  toggleEditProfile(): void {
    this.isEditingProfile = true;
    this.profileForm.enable();
    this.successMessage = '';
    this.errorMessage = '';
  }

  cancelEditProfile(): void {
    this.isEditingProfile = false;
    this.profileForm.disable();
    this.successMessage = '';
    this.errorMessage = '';
    this.selectedFile = null;
    this.profileImagePreview = null;
    if (this.profile) {
      this.profileForm.patchValue({
        firstName: this.profile.firstName ?? '',
        lastName: this.profile.lastName ?? '',
        phoneNumber: this.profile.phoneNumber ?? '',
        lang: this.profile.lang ?? 'en',
        showProfile: this.profile.ShowProfile ?? 'public',
        aboutMe: this.profile.aboutMe ?? '',
        location: this.profile.location ?? '',
        numberOfPropertiesOwned: this.profile.numberOfPropertiesOwned ?? '',
        notificationEmail: this.profile.notificationSettings?.email ?? true,
        notificationApp: this.profile.notificationSettings?.app ?? true,
      });
    }
  }

  async requestPayout(): Promise<void> {
    if (this.payoutForm.invalid) {
      this.payoutForm.markAllAsTouched();
      return;
    }

    this.submitting = true;
    this.payoutMessage = '';
    this.errorMessage = '';

    try {
      const amount = Number(this.payoutForm.value.amount);
      await this.dashboardService.requestPayout(amount);
      this.payoutMessage = 'ownerPortalDashboard.wallet.payoutSuccess';
      this.payoutForm.reset();
    } catch {
      this.errorMessage = 'ownerPortalDashboard.wallet.payoutError';
    } finally {
      this.submitting = false;
    }
  }

  trackByTransaction(index: number): number {
    return index;
  }

  openEditUnit(unit: any): void {
    this.router.navigate(['/dashboard/unit/edit', unit._id], {
      state: { unit },
      queryParams: { ownerType: unit.ownerType ?? '' },
    });
  }

  openPricing(unit: any): void {
    this.pricingUnitId = unit._id;
  }

  closePricing(): void {
    this.pricingUnitId = null;
  }

  openAvailabilityPanel(unit: any): void {
    this.availabilityUnitId = unit._id;
  }

  closeAvailabilityPanel(): void {
    this.availabilityUnitId = null;
  }
}
